
import type { PerformanceData, StudyHabit } from '../types';

/**
 * Identifies time-of-day patterns in user performance
 */
export const getTimeOfDayInsights = (performanceData: PerformanceData[]): { 
  timeComparison: string | null;
  morningAvg: number;
  eveningAvg: number; 
  difference: number;
} => {
  if (performanceData.length < 3) {
    return { timeComparison: null, morningAvg: 0, eveningAvg: 0, difference: 0 };
  }

  // Separate performance by time of day
  const morningScores = performanceData
    .filter(d => new Date(d.created_at).getHours() < 12)
    .map(d => d.score);
  
  const eveningScores = performanceData
    .filter(d => new Date(d.created_at).getHours() >= 12)
    .map(d => d.score);

  // Only compare if enough data exists
  if (morningScores.length < 2 || eveningScores.length < 2) {
    return { timeComparison: null, morningAvg: 0, eveningAvg: 0, difference: 0 };
  }

  const morningAvg = morningScores.reduce((a, b) => a + b, 0) / morningScores.length;
  const eveningAvg = eveningScores.reduce((a, b) => a + b, 0) / eveningScores.length;
  const difference = Math.abs(morningAvg - eveningAvg);
  const percentDifference = Math.round((difference / ((morningAvg + eveningAvg) / 2)) * 100);

  if (difference < 5) {
    return { 
      timeComparison: null,
      morningAvg,
      eveningAvg,
      difference
    };
  }

  const betterTime = morningAvg > eveningAvg ? 'morning' : 'evening';
  
  return {
    timeComparison: `You perform ${percentDifference}% better in ${betterTime} sessions!`,
    morningAvg,
    eveningAvg,
    difference
  };
};

/**
 * Analyzes confidence vs. accuracy discrepancies
 */
export const getConfidenceAccuracyGaps = (
  performanceData: PerformanceData[]
): { subject: string; confidence: number; accuracy: number; gap: number }[] => {
  if (performanceData.length < 3) return [];
  
  // Group by subject
  const subjectPerformance = performanceData.reduce((acc, curr) => {
    if (!curr.subject) return acc;
    
    if (!acc[curr.subject]) {
      acc[curr.subject] = {
        times: [],
        scores: [],
        correct: [],
        total: []
      };
    }
    
    acc[curr.subject].times.push(curr.created_at);
    acc[curr.subject].scores.push(curr.score);
    acc[curr.subject].correct.push(curr.correct_answers);
    acc[curr.subject].total.push(curr.total_questions);
    
    return acc;
  }, {} as Record<string, { times: string[], scores: number[], correct: number[], total: number[] }>);
  
  return Object.entries(subjectPerformance)
    .map(([subject, data]) => {
      // Calculate average time spent per question as proxy for confidence
      const avgScore = data.scores.reduce((a, b) => a + b, 0) / data.scores.length;
      
      // Calculate accuracy
      const totalCorrect = data.correct.reduce((a, b) => a + b, 0);
      const totalQuestions = data.total.reduce((a, b) => a + b, 0);
      const accuracy = totalQuestions ? (totalCorrect / totalQuestions) * 100 : 0;
      
      // Confidence proxy: Higher scores but lower accuracy means high confidence but poor accuracy
      const confidenceProxy = Math.min(100, avgScore + 10); // Inflated confidence score
      
      return {
        subject,
        confidence: confidenceProxy,
        accuracy,
        gap: Math.abs(confidenceProxy - accuracy)
      };
    })
    .filter(item => item.gap > 15) // Only show significant gaps
    .sort((a, b) => b.gap - a.gap);
};

/**
 * Analyzes learning patterns for streaks, consistency, and habits
 */
export const getLearningHabits = (performanceData: PerformanceData[]): StudyHabit[] => {
  if (performanceData.length < 5) return [];
  
  const habits: StudyHabit[] = [];
  
  // Check consistency of study sessions
  const dates = performanceData.map(d => new Date(d.created_at).toDateString());
  const uniqueDates = [...new Set(dates)];
  const daysStudied = uniqueDates.length;
  
  // Calculate date range
  const oldestDate = new Date(Math.min(...performanceData.map(d => new Date(d.created_at).getTime())));
  const newestDate = new Date(Math.max(...performanceData.map(d => new Date(d.created_at).getTime())));
  const dayDiff = Math.floor((newestDate.getTime() - oldestDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  
  const consistency = daysStudied / dayDiff;
  
  if (dayDiff >= 7) { // Only evaluate if at least a week of data
    if (consistency >= 0.7) {
      habits.push({
        title: 'Consistent Learning',
        description: `Great job! You've studied on ${Math.round(consistency * 100)}% of days in your learning period.`,
        status: 'good'
      });
    } else if (consistency <= 0.3) {
      habits.push({
        title: 'Sporadic Learning Pattern',
        description: 'More frequent, consistent study sessions would help your retention.',
        status: 'needs_work'
      });
    }
  }
  
  // Check difficulty progression
  const difficulties = performanceData.map(d => d.difficulty);
  const easyCount = difficulties.filter(d => d === 'easy').length;
  const mediumCount = difficulties.filter(d => d === 'medium').length;
  const hardCount = difficulties.filter(d => d === 'hard').length;
  
  const totalCount = difficulties.length;
  
  if (totalCount >= 10) {
    if (easyCount / totalCount > 0.7) {
      habits.push({
        title: 'Challenge Yourself',
        description: "You're comfortable with easy questions. Try increasing the difficulty!",
        status: 'needs_work'
      });
    } else if (hardCount / totalCount > 0.3 && hardCount / totalCount < 0.5) {
      habits.push({
        title: 'Great Challenge Balance',
        description: "You're pushing yourself with hard questions while maintaining confidence.",
        status: 'good'
      });
    }
  }
  
  return habits;
};

/**
 * Identifies improvement areas based on recent performance
 */
export const getImprovementAreas = (performanceData: PerformanceData[]): { 
  subject: string;
  recentScore: number; 
  trend: 'improving' | 'declining' | 'stable';
  message: string;
}[] => {
  if (performanceData.length < 5) return [];
  
  const subjectData = performanceData.reduce((acc, curr) => {
    if (!curr.subject) return acc;
    
    if (!acc[curr.subject]) {
      acc[curr.subject] = [];
    }
    
    acc[curr.subject].push({
      score: curr.score,
      date: new Date(curr.created_at)
    });
    
    return acc;
  }, {} as Record<string, { score: number; date: Date }[]>);
  
  return Object.entries(subjectData)
    .map(([subject, data]) => {
      // Sort by date
      data.sort((a, b) => a.date.getTime() - b.date.getTime());
      
      // Get recent score
      const recentScore = data[data.length - 1].score;
      
      // Need at least 3 entries to determine trend
      if (data.length < 3) {
        return {
          subject,
          recentScore,
          trend: 'stable' as const,
          message: `Keep practicing ${subject} to establish a learning trend.`
        };
      }
      
      // Look at last 3 entries
      const recentEntries = data.slice(-3);
      const firstScore = recentEntries[0].score;
      const lastScore = recentEntries[recentEntries.length - 1].score;
      const difference = lastScore - firstScore;
      
      let trend: 'improving' | 'declining' | 'stable';
      let message: string;
      
      if (difference > 10) {
        trend = 'improving';
        message = `Great job! You're showing strong improvement in ${subject}.`;
      } else if (difference < -10) {
        trend = 'declining';
        message = `Your recent ${subject} scores have dropped. Let's focus on reviewing the fundamentals.`;
      } else {
        trend = 'stable';
        message = recentScore > 70
          ? `You're maintaining good performance in ${subject}.`
          : `Try new learning methods to improve your ${subject} scores.`;
      }
      
      return { subject, recentScore, trend, message };
    })
    .sort((a, b) => {
      // Prioritize declining subjects first
      if (a.trend === 'declining' && b.trend !== 'declining') return -1;
      if (a.trend !== 'declining' && b.trend === 'declining') return 1;
      return 0;
    });
};
